import {
  createStyles,
  makeStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ErrorStatus, SuccessStatus, WarningStatus } from '../../../../icons/StatusIcons';
import { TwilioStatus, TwilioAPIStatus, useAppStateContext } from '../../../AppStateProvider/AppStateProvider';
import { getCompatibilityDescription, getNATTypeDescription } from '../../../../utils/stunTesting';
import { getConnectionTypeDescription, getConnectionQualityAssessment } from '../../../../utils/iceCandidatePairTesting';

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      width: '100%',
      maxWidth: '600px',
    },
    content: {
      padding: '3em',
      [theme.breakpoints.down('sm')]: {
        padding: '2em',
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        marginRight: '0.3em',
      },
    },
  })
);

const serviceStatusObj = {
  operational: { status: 'Up', icon: <SuccessStatus /> },
  major_outage: { status: 'Major Outage', icon: <ErrorStatus /> },
  partial_outage: { status: 'Partial Outage', icon: <WarningStatus /> },
  degraded_performance: { status: 'Degraded', icon: <WarningStatus /> },
};

export function TwilioStatusRow({ status, serviceName }: { status?: TwilioAPIStatus; serviceName: string }) {
  const classes = useStyles();
  const serviceStatus = status ? serviceStatusObj[status] : null;

  return (
    <TableRow>
      <TableCell>
        <div className={classes.iconContainer}>
          {serviceStatus?.icon}
          <Typography variant="body1">
            <strong>{serviceStatus?.status}</strong>
          </Typography>
        </div>
      </TableCell>
      <TableCell>
        <Typography variant="body1">{serviceName}</Typography>
      </TableCell>
    </TableRow>
  );
}

interface ConnectionModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  serviceStatuses: TwilioStatus | null;
  signalingGateway: string;
  turnServers: string;
}

export function ConnectionModal({
  isModalOpen,
  setIsModalOpen,
  serviceStatuses,
  signalingGateway,
  turnServers,
}: ConnectionModalProps) {
  const classes = useStyles();
  const { state } = useAppStateContext();

  // Get bitrate test information
  const bitrateReport = state.bitrateTest.report;
  const isPartialTest = bitrateReport && (bitrateReport as any).qualityMetrics?.cancelled;
  const qualityMetrics = isPartialTest ? (bitrateReport as any).qualityMetrics : null;

  // Helper function to format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} classes={{ paper: classes.modal }}>
        <DialogTitle disableTypography>
          <Typography variant="h3">Detailed connection information</Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Status</strong>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Type</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TwilioStatusRow status={serviceStatuses?.['Compositions']} serviceName="Compositions" />
              <TwilioStatusRow status={serviceStatuses?.['Go Rooms']} serviceName="Go Rooms" />
              <TwilioStatusRow status={serviceStatuses?.['Group Rooms']} serviceName="Group Rooms" />
              <TwilioStatusRow
                status={serviceStatuses?.['Network Traversal Service']}
                serviceName="Network Traversal"
              />
              <TwilioStatusRow status={serviceStatuses?.['Peer-to-Peer Rooms']} serviceName="Peer-to-Peer Rooms" />
              <TwilioStatusRow status={serviceStatuses?.['Recordings']} serviceName="Recordings" />
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    {signalingGateway === 'Reachable' ? <SuccessStatus /> : <ErrorStatus />}
                    <Typography variant="body1">
                      <strong>{signalingGateway}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">Signaling Gateway</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    {turnServers === 'Reachable' ? <SuccessStatus /> : <ErrorStatus />}
                    <Typography variant="body1">
                      <strong>{turnServers}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">TURN Servers</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* NAT/Firewall Analysis */}
          {state.stunTest.finished && (
            <div style={{ marginTop: '2em' }}>
              <Typography variant="h6" gutterBottom>
                NAT/Firewall Analysis
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <div className={classes.iconContainer}>
                        {state.stunTest.analysis ? (
                          state.stunTest.analysis.serversReachable >= 3 ? (
                            <SuccessStatus />
                          ) : state.stunTest.analysis.serversReachable >= 2 ? (
                            <WarningStatus />
                          ) : (
                            <ErrorStatus />
                          )
                        ) : (
                          <ErrorStatus />
                        )}
                        <Typography variant="body1">
                          <strong>
                            {state.stunTest.analysis
                              ? `${state.stunTest.analysis.serversReachable}/${state.stunTest.analysis.totalServers} servers`
                              : 'Failed'}
                          </strong>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">STUN Connectivity</Typography>
                    </TableCell>
                  </TableRow>
                  
                  {state.stunTest.analysis && (
                    <>
                      <TableRow>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            {state.stunTest.analysis.natType === 'full-cone' || state.stunTest.analysis.natType === 'restricted' || state.stunTest.analysis.natType === 'port-restricted' ? (
                              <SuccessStatus />
                            ) : state.stunTest.analysis.natType === 'symmetric' ? (
                              <ErrorStatus />
                            ) : (
                              <WarningStatus />
                            )}
                            <Typography variant="body1">
                              <strong>{getNATTypeDescription(state.stunTest.analysis.natType)}</strong>
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">NAT Type</Typography>
                        </TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            {state.stunTest.analysis.cloudtalkCompatibility === 'compatible' ? (
                              <SuccessStatus />
                            ) : state.stunTest.analysis.cloudtalkCompatibility === 'usually-works' ||
                              state.stunTest.analysis.cloudtalkCompatibility === 'turn-required' ? (
                              <WarningStatus />
                            ) : (
                              <ErrorStatus />
                            )}
                            <Typography variant="body1">
                              <strong>
                                {state.stunTest.analysis.cloudtalkCompatibility === 'compatible'
                                  ? 'Fully Compatible'
                                  : state.stunTest.analysis.cloudtalkCompatibility === 'usually-works'
                                  ? 'Usually Works'
                                  : state.stunTest.analysis.cloudtalkCompatibility === 'turn-required'
                                  ? 'Works via TURN Relay'
                                  : 'Issues Detected'}
                              </strong>
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">CloudTalk Compatibility</Typography>
                        </TableCell>
                      </TableRow>
                      
                      {state.stunTest.analysis.externalIP && (
                        <TableRow>
                          <TableCell>
                            <div className={classes.iconContainer}>
                              {/* Consistent IP across all servers is what matters.
                                  Port variation is normal for port-restricted NAT. */}
                              <SuccessStatus />
                              <Typography variant="body1">
                                <strong>{state.stunTest.analysis.externalIP}</strong>
                              </Typography>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1">External IP Address</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                  
                  {state.stunTest.error && (
                    <TableRow>
                      <TableCell>
                        <div className={classes.iconContainer}>
                          <ErrorStatus />
                          <Typography variant="body1">
                            <strong>Test Failed</strong>
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">Error: {state.stunTest.error.message}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {state.stunTest.analysis && (
                <Typography variant="body2" style={{ marginTop: '1em', fontStyle: 'italic' }}>
                  {getCompatibilityDescription(state.stunTest.analysis.cloudtalkCompatibility)}
                </Typography>
              )}
            </div>
          )}

          {/* ICE Candidate Pair Analysis */}
          {state.iceTest.finished && (
            <div style={{ marginTop: '2em' }}>
              <Typography variant="h6" gutterBottom>
                Connection Path Analysis
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <div className={classes.iconContainer}>
                        {state.iceTest.result?.success ? (
                          <SuccessStatus />
                        ) : (
                          <ErrorStatus />
                        )}
                        <Typography variant="body1">
                          <strong>
                            {state.iceTest.result?.success ? 'Success' : 'Failed'}
                          </strong>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">Connection Establishment</Typography>
                    </TableCell>
                  </TableRow>
                  
                  {state.iceTest.result?.success && state.iceTest.result.selectedPair && (
                    <>
                      <TableRow>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            {(() => {
                              const quality = getConnectionQualityAssessment(state.iceTest.result.selectedPair.connectionType);
                              return quality.quality === 'excellent' ? (
                                <SuccessStatus />
                              ) : quality.quality === 'good' ? (
                                <SuccessStatus />
                              ) : quality.quality === 'fair' ? (
                                <WarningStatus />
                              ) : (
                                <ErrorStatus />
                              );
                            })()}
                            <Typography variant="body1">
                              <strong>{getConnectionTypeDescription(state.iceTest.result.selectedPair.connectionType)}</strong>
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">Selected Connection Type</Typography>
                        </TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            {state.iceTest.result.connectionEstablishmentTime < 5000 ? (
                              <SuccessStatus />
                            ) : state.iceTest.result.connectionEstablishmentTime < 10000 ? (
                              <WarningStatus />
                            ) : (
                              <ErrorStatus />
                            )}
                            <Typography variant="body1">
                              <strong>{Math.round(state.iceTest.result.connectionEstablishmentTime / 1000 * 100) / 100}s</strong>
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">Connection Setup Time</Typography>
                        </TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            {state.iceTest.result.iceGatheringTime < 3000 ? (
                              <SuccessStatus />
                            ) : state.iceTest.result.iceGatheringTime < 6000 ? (
                              <WarningStatus />
                            ) : (
                              <ErrorStatus />
                            )}
                            <Typography variant="body1">
                              <strong>{Math.round(state.iceTest.result.iceGatheringTime / 1000 * 100) / 100}s</strong>
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">ICE Gathering Time</Typography>
                        </TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            <SuccessStatus />
                            <Typography variant="body1">
                              <strong>
                                {state.iceTest.result.selectedPair.localCandidate.type} → {state.iceTest.result.selectedPair.remoteCandidate.type}
                              </strong>
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">Candidate Types Used</Typography>
                        </TableCell>
                      </TableRow>
                      
                      {state.iceTest.result.selectedPair.currentRoundTripTime > 0 && (
                        <TableRow>
                          <TableCell>
                            <div className={classes.iconContainer}>
                              {state.iceTest.result.selectedPair.currentRoundTripTime < 0.1 ? (
                                <SuccessStatus />
                              ) : state.iceTest.result.selectedPair.currentRoundTripTime < 0.2 ? (
                                <WarningStatus />
                              ) : (
                                <ErrorStatus />
                              )}
                              <Typography variant="body1">
                                <strong>{Math.round(state.iceTest.result.selectedPair.currentRoundTripTime * 1000)}ms</strong>
                              </Typography>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1">Connection Round Trip Time</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                  
                  {state.iceTest.error && (
                    <TableRow>
                      <TableCell>
                        <div className={classes.iconContainer}>
                          <ErrorStatus />
                          <Typography variant="body1">
                            <strong>Test Failed</strong>
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">Error: {state.iceTest.error.message}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {state.iceTest.result?.success && state.iceTest.result.selectedPair && (
                <Typography variant="body2" style={{ marginTop: '1em', fontStyle: 'italic' }}>
                  {getConnectionQualityAssessment(state.iceTest.result.selectedPair.connectionType).description}
                </Typography>
              )}
            </div>
          )}

          {/* Network Quality Test Results */}
          {bitrateReport && (
            <div style={{ marginTop: '2em' }}>
              <Typography variant="h6" gutterBottom>
                Network Quality Test Results
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body1">
                        <strong>Average Bitrate:</strong>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">{Math.round(bitrateReport.averageBitrate || 0)} kbps</Typography>
                    </TableCell>
                  </TableRow>

                  {qualityMetrics && (
                    <>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body1">
                            <strong>Test Duration:</strong>
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {formatTime(qualityMetrics.testDuration)}
                            {isPartialTest && ' (completed early)'}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography variant="body1">
                            <strong>Connection Stability:</strong>
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <div className={classes.iconContainer}>
                            {qualityMetrics.bitrateStability > 80 ? (
                              <SuccessStatus />
                            ) : qualityMetrics.bitrateStability > 60 ? (
                              <WarningStatus />
                            ) : (
                              <ErrorStatus />
                            )}
                            <Typography variant="body1">
                              <strong>{Math.round(qualityMetrics.bitrateStability)}%</strong>
                            </Typography>
                          </div>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography variant="body1">
                            <strong>Data Points Collected:</strong>
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">{qualityMetrics.totalSamples} samples</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
